import genericAvatar from "assets/images/generic_avatar.jpg";
import { ErrorModal, PhotoUploader } from "components";
import { getDownloadURL } from "firebase/storage";
import { emptyHiredPlacement, HiredTeacherPlacements } from "models";
import React, { useState } from "react";
import { Button, Col, Form, Modal } from "react-bootstrap";
import Img from "react-cool-img";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { addHiredPlacementStory, updateHiredPlacementStory, uploadProfilePhoto } from "services";
import { generateFullName } from "utilities";

interface AddEditHiredPlacementModalProps {
  /** Whether the modal should be shown. */
  show: boolean;

  setShow: React.Dispatch<React.SetStateAction<boolean>>;

  /** Function to be called when canceling or closing the modal. */
  /** The alum to edit, or undefined if creating a new alum. */
  currentHiredPlacement: HiredTeacherPlacements | undefined;
}

/** Modal for adding/editing curated alumni. */
export function AddEditHiredPlacementModal({
  show,
  setShow,
  currentHiredPlacement,
}: AddEditHiredPlacementModalProps): JSX.Element {
  const [editedHiredPlacement, setEditedHiredPlacement] = useState<HiredTeacherPlacements>(
    currentHiredPlacement ? { ...currentHiredPlacement } : { ...emptyHiredPlacement },
  );

  /**
   * A function that will return the address/location of the current hiredPlacement, or
   * an empty string if null
   * @returns The location of the current (unedited) hiredPlacement, or an empty string if it is a new hiredPlacement
   */
  function setHiredPlacementLocation() {
    if (currentHiredPlacement) {
      return currentHiredPlacement.schoolAddress;
    }
    return "";
  }

  // const [isShowingNewHiredPlacementModal, setIsShowingNewHiredPlacementModal] = useState(show);
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState<File | undefined>(undefined);
  const [isReplacingPhoto, setIsReplacingPhoto] = useState(false);
  const isNew = currentHiredPlacement === undefined;
  const [error, setError] = useState<Error | undefined>(undefined);
  const [address, setAddress] = useState(setHiredPlacementLocation());
  editedHiredPlacement.schoolAddress = address;
  editedHiredPlacement.type = "hired";
  const [coordinates, setCoordinates] = React.useState({
    lat: 42.979167,
    lng: -85.808891,
  });
  editedHiredPlacement.longitude = coordinates.lng;
  editedHiredPlacement.latitude = coordinates.lat;

  const handleSelect = async (mapvalue: string) => {
    const results = await geocodeByAddress(mapvalue);
    const latLng = await getLatLng(results[0]);
    setAddress(mapvalue);
    setCoordinates(latLng);
  };

  const handleClose = () => {
    setShow(false);
  };

  /**
   * This method will handle the submit function
   * First checking if the admin has submitted all the required fields
   * Then upload the photo to firebase storage and upload photo url to firestore with the profile
   * Finally upload the profile to firestore
   */
  async function submitHiredPlacement(event: {
    currentTarget: any;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.preventDefault(); // prevents the page from reloading immediately on submit

      if (uploadedPhoto !== undefined) {
        const uploadTask = uploadProfilePhoto(uploadedPhoto, editedHiredPlacement);
        await uploadTask;
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        editedHiredPlacement.photoUrl = url;
      }

      const submitFn: () => Promise<unknown> = isNew
        ? () => addHiredPlacementStory(editedHiredPlacement)
        : () => updateHiredPlacementStory(editedHiredPlacement.id, editedHiredPlacement);

      submitFn()
        .catch(err => setError(err))
        .finally(() => {
          setIsSubmitting(false);
          // history.push("map");
          // history.push("map-view-admin");
          window.location.reload(); // this is not the best but my window was reloading so hard with historuy.push
        });
      setIsSubmitting(true);
    }
    setValidated(true);
  }

  return (
    <>
      <Modal show={show} size="lg" className="add-edit-alumni-modal">
        <Form noValidate validated={validated} onSubmit={submitHiredPlacement}>
          <Modal.Header closeButton={false}>
            <Modal.Title>
              {isNew
                ? "Add New Alumni"
                : `Edit ${generateFullName(
                    editedHiredPlacement.firstName,
                    editedHiredPlacement.lastName,
                  )}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <strong>Basic Info</strong>
            </Form.Group>
            <Form.Row>
              <Col>
                <HiredPlacementFormGroup
                  attribute="firstName"
                  label="First Name"
                  placeholder="e.g. Jane"
                  onChange={e =>
                    setEditedHiredPlacement({
                      ...editedHiredPlacement,
                      firstName: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedHiredPlacement.firstName}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid first name.
                </Form.Control.Feedback>
              </Col>
              <Col>
                <HiredPlacementFormGroup
                  attribute="lastName"
                  label="Last Name"
                  placeholder="e.g. Doe"
                  onChange={e =>
                    setEditedHiredPlacement({
                      ...editedHiredPlacement,
                      lastName: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedHiredPlacement.lastName}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid last name.
                </Form.Control.Feedback>
              </Col>
            </Form.Row>

            <Form.Row>
              <Col>
                <Form.Label>
                  {" "}
                  Major (while at Hope)
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={editedHiredPlacement.major}
                  onChange={e => {
                    setEditedHiredPlacement({ ...editedHiredPlacement, major: e.target.value });
                  }}
                  required
                >
                  <option value=""> Select Option</option>
                  <option value="elementary"> Elementary</option>
                  <option value="secondary">Secondary</option>
                  <option value="special-education">Special-Education</option>
                </Form.Control>
                <Form.Control.Feedback type="invalid">Please select a Major</Form.Control.Feedback>
              </Col>

              <Col>
                <HiredPlacementFormGroup
                  type="number"
                  attribute="gradYear"
                  label="Grad Year"
                  placeholder={`e.g. ${new Date().getFullYear()}`}
                  onChange={e =>
                    setEditedHiredPlacement({
                      ...editedHiredPlacement,
                      gradYear: parseInt(e.target.value, 10),
                    })
                  }
                  defaultValue={
                    editedHiredPlacement.gradYear ? editedHiredPlacement.gradYear.toString() : ""
                  }
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid last name.
                </Form.Control.Feedback>
              </Col>
            </Form.Row>
            <Form.Group>
              <Form.Label>Profile Photo</Form.Label>
              {!!editedHiredPlacement.photoUrl && !isReplacingPhoto ? (
                <Form.Row>
                  <Col xs={2}>
                    <Img
                      src={editedHiredPlacement.photoUrl || ""}
                      placeholder={genericAvatar}
                      alt={`${editedHiredPlacement.firstName} ${editedHiredPlacement.lastName}`}
                      width="100%"
                      loading="lazy"
                      className="img-circle"
                    />
                  </Col>
                  <Col className="d-flex align-items-center">
                    <Button variant="outline-secondary" onClick={() => setIsReplacingPhoto(true)}>
                      Replace
                    </Button>
                  </Col>
                </Form.Row>
              ) : (
                <PhotoUploader onDrop={(photos: File[]) => setUploadedPhoto(photos[0])} />
              )}
            </Form.Group>

            <Form.Row>
              <Col>
                <HiredPlacementFormGroup
                  attribute="schoolName"
                  label="School Name"
                  placeholder="e.g. Springfield Elementary"
                  onChange={e =>
                    setEditedHiredPlacement({
                      ...editedHiredPlacement,
                      schoolName: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedHiredPlacement.schoolName || ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid School Name
                </Form.Control.Feedback>
              </Col>

              <Col>
                <Form.Label>
                  School Address
                  <span className="text-danger"> *</span>
                </Form.Label>
                <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}>
                  {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div>
                      <Form.Control {...getInputProps({ placeholder: "Type address" })} required />
                      <div>
                        {loading ? <div>...loading</div> : null}

                        {suggestions.map(suggestion => {
                          const style = {
                            backgroundColor: suggestion.active ? "#41b6e6" : "#fff",
                          };

                          return (
                            <div {...getSuggestionItemProps(suggestion, { style })}>
                              {suggestion.description}
                            </div>
                          );
                        })}
                      </div>
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid School Address
                      </Form.Control.Feedback>
                    </div>
                  )}
                </PlacesAutocomplete>
              </Col>

              <Col>
                <HiredPlacementFormGroup
                  attribute="currentPosition"
                  label="Current Position"
                  placeholder="e.g. Special Education Teacher"
                  onChange={e =>
                    setEditedHiredPlacement({
                      ...editedHiredPlacement,
                      currentPosition: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedHiredPlacement.currentPosition || ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid School Address
                </Form.Control.Feedback>
              </Col>
            </Form.Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="outline-primary" type="submit" disabled={isSubmitting}>
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ErrorModal error={error} />
    </>
  );
}

interface HiredPlacementFormGroupProps {
  attribute: keyof HiredTeacherPlacements;
  type?: "text" | "number";
  as?: "textarea";
  label: string;
  placeholder: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
  defaultValue: string;
  required?: boolean;
  rows?: number;
}

function HiredPlacementFormGroup({
  attribute,
  label,
  placeholder,
  onChange,
  defaultValue,
  type,
  as,
  required,
  rows,
}: HiredPlacementFormGroupProps) {
  return (
    <Form.Group controlId={attribute}>
      <Form.Label>
        {label}
        {required && <span className="text-danger"> *</span>}
      </Form.Label>
      <Form.Control
        placeholder={placeholder}
        onChange={onChange}
        type={type || "text"}
        defaultValue={defaultValue}
        as={as}
        required={required}
        rows={rows}
      />
    </Form.Group>
  );
}
